import { useRef, useState } from 'react';
import { useLocale } from 'use-intl';
import { useTranslations } from '@/i18n/translations';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { RouterLinkAs } from '@/lib/router-link';
import {
  Body,
  Box,
  Button,
  Card,
  Eyebrow,
  Field,
  Heading,
  I,
  Input,
  Notice,
  PageHeader,
  RadioCard,
  RadioCardGroup,
  Row,
  Screen,
  ScreenBody,
  Select,
  Stack,
  StepBadge,
  Textarea,
} from '@leanin/ui';
import type { CircleFormat } from '@leanin/shared/circle-format';
import { BodyWithAside } from '@/components/page-shell';
import {
  GroupImageUploadField,
  type GroupImageUploadFieldHandle,
} from '@/components/groups/GroupImageUploadField';
import { PlacePicker, type PlaceSelection } from '@/components/geo';
import { useCreateCircle } from '@/lib/mutations/groups';
import { useMyNetworks, useNetwork } from '@/lib/queries/groups';
import { useErrorMessage } from '@/lib/error-message';
import { useCapabilities } from '@/lib/use-session';
import {
  circleSaveErrorMessage,
  isIndustrySpecificWithoutIndustry,
} from '@/components/circles/circle-save-error';
import {
  CircleIndustryFields,
  NO_CIRCLE_INDUSTRY,
  type CircleIndustry,
} from '@/components/circles/CircleIndustryFields';
import { LanguageSelect } from '@/components/circles/LanguageSelect';
import {
  CircleLeaderFactsFields,
  NO_CIRCLE_LEADER_FACTS,
  parseCircleLeaderFacts,
  type CircleLeaderFacts,
  type CircleLeaderFactsErrors,
  useDiscussionTopics,
} from '@/components/circles/CircleLeaderFactsFields';
import { useFocusFirstError } from '@/components/form/use-focus-first-error';

type CircleNewSearch = {
  // Optional — when set, the form preselects this network in the
  // "add to your Network" field. Drives a contextual back link and, when
  // the selection is kept, a redirect back to the network admin Circles
  // tab on success.
  parentNetworkId?: string;
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const Route = createFileRoute('/_authed/circles/new')({
  validateSearch: (raw: Record<string, unknown>): CircleNewSearch => {
    const v = raw.parentNetworkId;
    // Only accept a well-formed uuid; ignore anything else silently so a
    // malformed link drops back to the standard free-floating form.
    return typeof v === 'string' && UUID_RE.test(v)
      ? { parentNetworkId: v }
      : {};
  },
  component: CircleCreatePage,
});

// Mirrors the onboarding step (/onboarding/cl/create-circle): two cards,
// "The basics" + "How you'll meet".

type YesNo = 'yes' | 'no' | null;

type RequiredField = 'name' | 'place' | 'affiliated' | 'company' | 'openToNew';

const NONE_MISSING: ReadonlySet<RequiredField> = new Set();

type RequiredAnswers = {
  readonly name: string;
  readonly place: PlaceSelection;
  readonly affiliation:
    | { readonly affiliatedWithCompany: true; readonly affiliatedCompany: string }
    | { readonly affiliatedWithCompany: false; readonly affiliatedCompany: null };
  readonly joinPolicy: 'request' | 'invite_only';
};

function parseRequiredAnswers(d: {
  readonly name: string;
  readonly place: PlaceSelection | null;
  readonly affiliated: YesNo;
  readonly company: string;
  readonly openToNew: YesNo;
}):
  | { readonly ok: true; readonly value: RequiredAnswers }
  | { readonly ok: false; readonly missing: ReadonlySet<RequiredField> } {
  const name = d.name.trim();
  const company = d.company.trim();
  const affiliation =
    d.affiliated === 'no'
      ? { affiliatedWithCompany: false as const, affiliatedCompany: null }
      : d.affiliated === 'yes' && company.length > 0
        ? { affiliatedWithCompany: true as const, affiliatedCompany: company }
        : null;
  if (name.length > 0 && d.place !== null && affiliation !== null && d.openToNew !== null) {
    return {
      ok: true,
      // "Open to new members? Yes" → request-to-join. "No" → invite-only.
      value: { name, place: d.place, affiliation, joinPolicy: d.openToNew === 'yes' ? 'request' : 'invite_only' },
    };
  }
  return {
    ok: false,
    missing: new Set<RequiredField>([
      ...(name.length === 0 ? (['name'] as const) : []),
      ...(d.place === null ? (['place'] as const) : []),
      ...(d.affiliated === null ? (['affiliated'] as const) : []),
      ...(d.affiliated === 'yes' && company.length === 0 ? (['company'] as const) : []),
      ...(d.openToNew === null ? (['openToNew'] as const) : []),
    ]),
  };
}

function CircleCreatePage() {
  const tc = useTranslations('Circles');
  const locale = useLocale();
  const describeError = useErrorMessage();
  const profileFields = useCapabilities()?.circleProfileFields === true;
  const { parentNetworkId } = Route.useSearch();
  const tips: ReadonlyArray<{ t: string; s: string }> = [
    { t: tc('new.tip1Title'), s: tc('new.tip1Body') },
    { t: tc('new.tip2Title'), s: tc('new.tip2Body') },
    { t: tc('new.tip3Title'), s: tc('new.tip3Body') },
    { t: tc('new.tip4Title'), s: tc('new.tip4Body') },
    { t: tc('new.tip5Title'), s: tc('new.tip5Body') },
  ];
  // Resolve the parent network (when the page was launched from a network
  // admin surface) so the back link can show the network's name. If the
  // network 404s or the viewer can't read it, we silently fall back to the
  // generic Circles label — the form itself still works and the server is
  // the final authority on the parent-link permission.
  const networkQuery = useNetwork(parentNetworkId ?? '', {
    enabled: parentNetworkId !== undefined,
  });
  const parentNetwork =
    parentNetworkId !== undefined && networkQuery.data !== undefined
      ? networkQuery.data
      : null;

  // Networks the viewer leads, offered as an optional "add to your Network"
  // field. Archived networks are excluded because the server's link
  // permission requires an active network — offering one would only
  // manufacture a 403 at publish time.
  const myNetworks = useMyNetworks();
  const ledNetworks = (myNetworks.data?.items ?? []).filter(
    (n) => n.role === 'leader' && n.archivedAt === null,
  );
  // A platform admin can arrive from any network's admin surface without
  // leading it; surface that network as an option too so the preselected
  // link is visible rather than silently applied.
  const networkOptions = [
    ...(parentNetworkId !== undefined &&
    parentNetwork !== null &&
    !ledNetworks.some((n) => n.id === parentNetworkId)
      ? [{ value: parentNetworkId, label: parentNetwork.name }]
      : []),
    ...ledNetworks.map((n) => ({ value: n.id, label: n.name })),
  ];
  const [selectedNetworkId, setSelectedNetworkId] = useState(
    parentNetworkId ?? '',
  );

  // Step 01 — The basics
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [place, setPlace] = useState<PlaceSelection | null>(null);
  const [affiliated, setAffiliated] = useState<YesNo>(null);
  const [company, setCompany] = useState('');
  // Step 02 — How you'll meet
  const [openToNew, setOpenToNew] = useState<YesNo>(null);
  const [format, setFormat] = useState<CircleFormat>('virtual');
  const [industry, setIndustry] = useState<CircleIndustry>(NO_CIRCLE_INDUSTRY);
  const [specificError, setSpecificError] = useState<string | null>(null);
  // The Circle most likely meets in the language its leader uses the app in.
  const [language, setLanguage] = useState<string | null>(locale);
  const [leaderFacts, setLeaderFacts] = useState<CircleLeaderFacts>(NO_CIRCLE_LEADER_FACTS);
  const [leaderFactsErrors, setLeaderFactsErrors] = useState<CircleLeaderFactsErrors>({});
  const discussionTopics = useDiscussionTopics({ enabled: profileFields });
  // Step 03 — Photos. Files are staged in the field handles; the circle
  // doesn't exist until Publish, so the field carries no real group id
  // up front. Publish resolves the id from createCircle, then asks each
  // field to commit using that id.
  const coverFieldRef = useRef<GroupImageUploadFieldHandle | null>(null);
  const [coverPending, setCoverPending] = useState(false);

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [committingPhotos, setCommittingPhotos] = useState(false);
  const navigate = useNavigate();
  const createCircle = useCreateCircle();

  // Publish stays enabled: a click with a required answer missing says so and
  // takes the leader to the first one, rather than a disabled button that
  // explains nothing. The marks clear as each answer is filled in.
  const [publishAttempted, setPublishAttempted] = useState(false);
  const required = parseRequiredAnswers({ name, place, affiliated, company, openToNew });
  const missing = publishAttempted && !required.ok ? required.missing : NONE_MISSING;
  const formMessage = submitError ?? (missing.size > 0 ? tc('new.validationRequired') : null);
  const fieldsRef = useRef<HTMLElement | null>(null);
  const focusFirstError = useFocusFirstError(fieldsRef);

  const onSubmit = async () => {
    setSubmitError(null);
    setSpecificError(null);
    setPublishAttempted(true);
    const facts = parseCircleLeaderFacts(leaderFacts, discussionTopics.activeIds);
    setLeaderFactsErrors(facts.ok ? {} : facts.errors);
    if (!required.ok || !facts.ok) {
      focusFirstError();
      return;
    }

    let detail;
    try {
      detail = await createCircle.mutateAsync({
        name: required.value.name,
        ...(description.trim().length > 0
          ? { description: description.trim() }
          : {}),
        place: required.value.place,
        topicIds: facts.value.topicIds,
        format,
        ...(facts.value.meetingCadence !== null ? { meetingCadence: facts.value.meetingCadence } : {}),
        ...(facts.value.targetSize !== null ? { targetSize: facts.value.targetSize } : {}),
        access: 'public',
        joinPolicy: required.value.joinPolicy,
        ...required.value.affiliation,
        // Hidden profile fields are left out, so the Circle keeps the
        // server's empty defaults (the language would otherwise be the
        // leader's locale).
        ...(profileFields
          ? {
              industryCode: industry.industryCode,
              industrySpecific: industry.industrySpecific,
              ...(language !== null ? { language } : {}),
            }
          : {}),
        ...(selectedNetworkId !== '' ? { parentNetworkId: selectedNetworkId } : {}),
      });
    } catch (err) {
      if (isIndustrySpecificWithoutIndustry(err)) {
        setSpecificError(tc('industryFields.errorRequiresIndustry'));
        return;
      }
      setSubmitError(circleSaveErrorMessage(err, describeError, tc('new.createFailed'), tc('errors.linkNotAllowed')));
      return;
    }
    // Photos are optional. If any are staged, commit them under the new
    // circle id (passed as the override so we don't have to wait for a
    // re-render of the field's `groupId` prop). A failed photo upload
    // doesn't block the navigate — the circle exists; the leader can
    // retry photos from circle settings. Just surface the message and
    // route forward.
    if (coverPending) {
      setCommittingPhotos(true);
      try {
        await coverFieldRef.current?.commit(detail.id);
      } catch (err) {
        setSubmitError(describeError(err, tc('new.photoUploadFailed')));
      } finally {
        setCommittingPhotos(false);
      }
    }
    // When the circle was launched from a network admin surface and kept
    // that network, the user expects to land back on that surface with
    // their new circle already in the list. Otherwise — including when a
    // leader picked a network in-form — route to the new circle's detail
    // page like a standalone create flow.
    if (parentNetworkId !== undefined && selectedNetworkId === parentNetworkId) {
      navigate({
        to: '/networks/$id/admin',
        params: { id: parentNetworkId },
        search: { section: 'circles' },
      });
    } else {
      navigate({ to: '/circles/$id', params: { id: detail.id } });
    }
  };
  const isPending = createCircle.isPending || committingPhotos;

  // When launched from a network admin surface, the back link and cancel
  // button retarget to that surface and the back-link label doubles as
  // contextual breadcrumb ("Lean In Saigon ←").
  const cancelHref =
    parentNetworkId !== undefined
      ? `/networks/${parentNetworkId}/admin?section=circles`
      : '/circles';
  const backLabel = parentNetwork !== null ? parentNetwork.name : tc('new.backCircles');

  return (
    <Screen>
      <ScreenBody gap="xl">
        <PageHeader
          eyebrow={tc('new.eyebrow')}
          title={tc('new.title')}
          subtitle={tc('new.subtitle')}
          back={{ label: backLabel, href: cancelHref }}
          actions={
            <>
              <Button href={cancelHref} linkAs={RouterLinkAs} variant="ghost">{tc('new.cancel')}</Button>
              <Button
                variant="primary"
                iconRight={I.arrow}
                onClick={onSubmit}
                loading={isPending}
              >
                {tc('new.publish')}
              </Button>
            </>
          }
        />
        <BodyWithAside
          asideWidth={380}
          aside={
            <Stack gap="lg">
              <Card pad="default">
                <Stack gap="lg">
                  <Stack gap="xs">
                    <Eyebrow tone="accent">{tc('new.tipsEyebrow')}</Eyebrow>
                    <Heading level={3}>
                      {tc('new.tipsTitle')}
                    </Heading>
                  </Stack>
                  <Stack gap="md">
                    {tips.map((tip, i) => (
                      <Row key={i} gap="sm" align="flex-start">
                        <StepBadge state="current" n={i + 1} size="sm" />
                        <Stack gap="xs">
                          <Heading level={5}>{tip.t}</Heading>
                          <Body size="sm" color="mute">
                            {tip.s}
                          </Body>
                        </Stack>
                      </Row>
                    ))}
                  </Stack>
                </Stack>
              </Card>
            </Stack>
          }
        >
          {/* MAIN */}
          <Stack gap="lg" ref={fieldsRef}>
            {/* 01 · The basics */}
            <Card pad="spacious">
              <Stack gap="lg">
                <Stack gap="xs">
                  <Eyebrow>{tc('new.step01')}</Eyebrow>
                  <Eyebrow tone="accent">{tc('new.step01Title')}</Eyebrow>
                </Stack>
                <Field required>
                  <Field.Label>{tc('new.nameLabel')}</Field.Label>
                  <Field.Description>
                    {tc('new.nameDescription')}
                  </Field.Description>
                  <Field.Control>
                    <Input
                      aria-invalid={missing.has('name') || undefined}
                      placeholder={tc('new.namePlaceholder')}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      maxLength={120}
                    />
                  </Field.Control>
                </Field>
                <Field>
                  <Field.Label>{tc('new.descriptionLabel')}</Field.Label>
                  <Field.Description>
                    {tc('new.descriptionDescription')}
                  </Field.Description>
                  <Field.Control>
                    <Textarea
                      rows={2}
                      placeholder={tc('new.descriptionPlaceholder')}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      maxLength={2000}
                    />
                  </Field.Control>
                </Field>
                <Field required>
                  <Field.Label>{tc('new.locationLabel')}</Field.Label>
                  <Field.Description>
                    {tc('new.locationDescription')}
                  </Field.Description>
                  <Field.Control>
                    <PlacePicker
                      mode="city"
                      value={place}
                      onChange={setPlace}
                      placeholder={tc('new.locationPlaceholder')}
                      aria-invalid={missing.has('place') || undefined}
                    />
                  </Field.Control>
                </Field>
                <Field required>
                  <Field.Label>{tc('new.affiliatedQuestion')}</Field.Label>
                  <Field.Description>
                    {tc('new.affiliatedDescription')}
                  </Field.Description>
                  <Field.Control>
                    <RadioCardGroup
                      value={affiliated ?? ''}
                      onValueChange={(v) => setAffiliated(v as 'yes' | 'no')}
                      columns={2}
                      aria-label={tc('new.affiliatedQuestion')}
                      aria-invalid={missing.has('affiliated')}
                    >
                      <RadioCard value="yes" title={tc('new.yes')} />
                      <RadioCard value="no" title={tc('new.no')} />
                    </RadioCardGroup>
                  </Field.Control>
                </Field>
                {affiliated === 'yes' ? (
                  <Field label={tc('new.whichCompany')} required>
                    <Input
                      aria-invalid={missing.has('company') || undefined}
                      placeholder={tc('new.companyPlaceholder')}
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                    />
                  </Field>
                ) : null}
                {networkOptions.length > 0 ? (
                  <Field>
                    <Field.Label>{tc('new.networkLabel')}</Field.Label>
                    <Field.Description>
                      {tc('new.networkDescription')}
                    </Field.Description>
                    <Field.Control>
                      <Select
                        value={selectedNetworkId}
                        options={[
                          { value: '', label: tc('new.networkNone') },
                          ...networkOptions,
                        ]}
                        onChange={(e) => setSelectedNetworkId(e.target.value)}
                        aria-label={tc('new.networkLabel')}
                      />
                    </Field.Control>
                  </Field>
                ) : null}
              </Stack>
            </Card>

            {/* 02 · How you'll meet */}
            <Card pad="spacious">
              <Stack gap="lg">
                <Stack gap="xs">
                  <Eyebrow>{tc('new.step02')}</Eyebrow>
                  <Eyebrow tone="accent">{tc('new.step02Title')}</Eyebrow>
                </Stack>
                <Field required>
                  <Field.Label>{tc('new.openQuestion')}</Field.Label>
                  <Field.Description>
                    {tc('new.openDescription')}
                  </Field.Description>
                  <Field.Control>
                    <RadioCardGroup
                      value={openToNew ?? ''}
                      onValueChange={(v) => setOpenToNew(v as 'yes' | 'no')}
                      columns={2}
                      aria-label={tc('new.openQuestion')}
                      aria-invalid={missing.has('openToNew')}
                    >
                      <RadioCard value="yes" title={tc('new.yes')} />
                      <RadioCard value="no" title={tc('new.no')} />
                    </RadioCardGroup>
                  </Field.Control>
                </Field>
                <Field label={tc('new.meetFormatLabel')} required>
                  <RadioCardGroup
                    value={format}
                    onValueChange={(v) => setFormat(v as CircleFormat)}
                    columns={3}
                    aria-label={tc('new.meetingFormatAria')}
                  >
                    <RadioCard value="virtual" icon={I.globe} title={tc('new.formatVirtual')} />
                    <RadioCard value="in_person" icon={I.pin} title={tc('new.formatInPerson')} />
                    <RadioCard value="hybrid" icon={I.users} title={tc('new.formatHybrid')} />
                  </RadioCardGroup>
                </Field>
                {profileFields ? (
                  <>
                    <CircleIndustryFields
                      value={industry}
                      onChange={(next) => {
                        setIndustry(next);
                        setSpecificError(null);
                      }}
                      specificError={specificError}
                      disabled={isPending}
                    />
                    <Field label={tc('languageSelect.label')}>
                      <LanguageSelect value={language} onChange={setLanguage} disabled={isPending} />
                    </Field>
                    <CircleLeaderFactsFields
                      value={leaderFacts}
                      onChange={(next) => {
                        setLeaderFacts(next);
                        setLeaderFactsErrors({});
                      }}
                      errors={leaderFactsErrors}
                      topics={discussionTopics}
                      disabled={isPending}
                    />
                  </>
                ) : null}
              </Stack>
            </Card>

            {/* 03 · Photos */}
            <Card pad="flush">
              <Stack p="xl" gap="xs">
                <Eyebrow>{tc('new.step03')}</Eyebrow>
                <Eyebrow tone="accent">{tc('new.step03Title')}</Eyebrow>
                <Body size="sm" color="mute">
                  {tc('new.photosDescription')}
                </Body>
              </Stack>
              <Box p="xl" style={{ borderTop: '1px solid var(--border)' }}>
                <GroupImageUploadField
                  ref={coverFieldRef}
                  groupKind="circle"
                  // Placeholder identifier — the field doesn't call presign
                  // until commit(), and Publish passes the freshly-created
                  // id as an override at that point.
                  groupId="pending"
                  groupName={name || tc('new.coverFallbackName')}
                  field="cover"
                  currentImageUrl={null}
                  eyebrowOverride={tc('new.coverEyebrow')}
                  onPendingChange={setCoverPending}
                />
              </Box>
            </Card>

            {formMessage !== null ? (
              <Notice intent="danger" dismissible={false} title={formMessage} />
            ) : null}

            <Row justify="space-between" align="center">
              <Body size="sm" color="mute">
                {tc('new.publishingHint')}
              </Body>
              <Row gap="sm">
                <Button href={cancelHref} linkAs={RouterLinkAs} variant="ghost">{tc('new.cancel')}</Button>
                <Button
                  variant="primary"
                  iconRight={I.arrow}
                  onClick={onSubmit}
                  loading={isPending}
                >
                  {tc('new.publish')}
                </Button>
              </Row>
            </Row>
          </Stack>
        </BodyWithAside>
      </ScreenBody>
    </Screen>
  );
}
